// MemoryResultCard (plan todo 23) — FoodMemoryCard-style list for
// searchFoodMemory / listFoodMemories / saveFoodMemory results. Edit and Log
// actions stay behind the approval flow by asking the agent via a normal
// user message (writes are never performed directly from chat).

import { Brain } from "lucide-react";
import { formatNumber } from "../../lib/format";
import { cn } from "../../lib/cn";
import type { MemoryResultData } from "./card-data";
import { CARD_BUTTON_BASE, ChatCardShell } from "./card-ui";

const PREVIEW_LIMIT = 3;

export interface MemoryResultCardProps {
  readonly data: MemoryResultData;
  readonly onSendMessage?: (text: string) => void;
}

const confidenceClasses: Record<string, string> = {
  high: "bg-accent-light text-accent",
  medium: "bg-warning/10 text-warning",
  low: "bg-muted text-text-muted",
};

export function MemoryResultCard({ data, onSendMessage }: MemoryResultCardProps) {
  const visible = data.memories.slice(0, PREVIEW_LIMIT);
  const hiddenCount = data.memories.length - visible.length;
  return (
    <ChatCardShell
      icon={Brain}
      title="Food memory"
      testId="chat-card-memory-result"
    >
      {data.memories.length === 0 ? (
        <p className="text-sm leading-relaxed text-text-muted" data-testid="memory-result-empty">
          No saved memories matched. Log a meal and ask BiteTrack to remember it.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="memory-result-list">
          {visible.map((memory, index) => (
            <li
              key={memory.id ?? `${memory.foodName}-${index}`}
              className="flex items-center justify-between gap-2 rounded-xl bg-muted/60 px-3 py-2"
              data-testid={`memory-result-item-${index}`}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text">{memory.foodName}</div>
                <div className="text-xs text-text-muted">
                  {memory.servingSize !== undefined ? `${memory.servingSize} · ` : ""}
                  {formatNumber(memory.calories)} kcal
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {memory.confidence !== undefined && (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-medium uppercase",
                      confidenceClasses[memory.confidence] ?? confidenceClasses.low,
                    )}
                  >
                    {memory.confidence}
                  </span>
                )}
                {onSendMessage !== undefined && (
                  <>
                    <button
                      type="button"
                      data-testid={`memory-log-btn-${index}`}
                      aria-label={`Log ${memory.foodName}`}
                      className={cn(CARD_BUTTON_BASE, "min-h-[44px] px-3 text-xs text-accent")}
                      onClick={() => onSendMessage(`Log ${memory.foodName}`)}
                    >
                      Log
                    </button>
                    <button
                      type="button"
                      data-testid={`memory-edit-btn-${index}`}
                      aria-label={`Edit ${memory.foodName}`}
                      className={cn(CARD_BUTTON_BASE, "min-h-[44px] px-3 text-xs text-text-muted")}
                      onClick={() => onSendMessage(`Update the ${memory.foodName} food memory`)}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="text-xs text-text-muted">
              +{hiddenCount} more memor{hiddenCount === 1 ? "y" : "ies"}
            </li>
          )}
        </ul>
      )}
    </ChatCardShell>
  );
}
